import type { MapCluster, MapPoint, MapViewport } from "@/lib/map-clusters";
import {
  clusterMapPoints,
  quantizeViewport,
  viewportsEqual,
} from "@/lib/map-clusters";
import { AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

export type MapClusterSummaryState = {
  mode: "pins" | "clusters";
  totalInView: number;
  clusterCount: number;
};

/** Automatic framing stays at neighborhood scale for a single point. */
const MAP_FIT_MAX_ZOOM = 15;
const MAP_FIT_MIN_PX = 32;

type MapFitPoint = MapPoint &
  Partial<Pick<MapCluster, "north" | "south" | "east" | "west">>;

function mapHasUsableSize(map: google.maps.Map): boolean {
  const div = map.getDiv?.();
  if (!div) return true;
  return (
    div.clientWidth >= MAP_FIT_MIN_PX && div.clientHeight >= MAP_FIT_MIN_PX
  );
}

function extendBoundsForFitPoint(
  bounds: google.maps.LatLngBounds,
  point: MapFitPoint,
) {
  const { north, south, east, west } = point;
  if (
    north != null &&
    south != null &&
    east != null &&
    west != null &&
    Number.isFinite(north) &&
    Number.isFinite(south) &&
    Number.isFinite(east) &&
    Number.isFinite(west)
  ) {
    bounds.extend({ lat: south, lng: west });
    bounds.extend({ lat: north, lng: east });
    return;
  }
  if (Number.isFinite(point.lat) && Number.isFinite(point.lng)) {
    bounds.extend({ lat: point.lat, lng: point.lng });
  }
}

export function useMapViewport(
  onChange?: (viewport: MapViewport) => void,
): MapViewport | null {
  const map = useMap();
  const [viewport, setViewport] = useState<MapViewport | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastNotifiedRef = useRef<MapViewport | null>(null);

  useEffect(() => {
    if (!map) return;
    let timer = 0;
    const emit = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const bounds = map.getBounds();
        if (!bounds || !mapHasUsableSize(map)) return;
        const northEast = bounds.getNorthEast();
        const southWest = bounds.getSouthWest();
        const next = quantizeViewport({
          north: northEast.lat(),
          south: southWest.lat(),
          east: northEast.lng(),
          west: southWest.lng(),
          zoom: map.getZoom() ?? 4,
        });
        if (viewportsEqual(lastNotifiedRef.current, next)) return;
        lastNotifiedRef.current = next;
        setViewport(next);
        onChangeRef.current?.(next);
      }, 250);
    };
    const listener = map.addListener("idle", emit);
    window.addEventListener("resize", emit);
    emit();
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", emit);
      listener.remove();
    };
  }, [map]);

  return viewport;
}

export function zoomMapToCluster(
  map: google.maps.Map | null,
  cluster: MapCluster,
) {
  if (!map) return;
  const currentZoom = map.getZoom() ?? 4;
  const bounds = new google.maps.LatLngBounds(
    { lat: cluster.south, lng: cluster.west },
    { lat: cluster.north, lng: cluster.east },
  );
  map.fitBounds(bounds, 48);
  google.maps.event.addListenerOnce(map, "idle", () => {
    const nextZoom = map.getZoom() ?? currentZoom;
    if (nextZoom < currentZoom + 1) {
      map.setZoom(Math.min(currentZoom + 2, 19));
    }
  });
}

export function MapCountClusterMarker({ cluster }: { cluster: MapCluster }) {
  const map = useMap();
  const size = Math.min(64, 28 + Math.round(Math.log10(cluster.count) * 14));
  return (
    <AdvancedMarker
      position={{ lat: cluster.lat, lng: cluster.lng }}
      onClick={() => zoomMapToCluster(map, cluster)}
      title={`${cluster.count.toLocaleString()} properties`}
    >
      <div
        aria-label={`${cluster.count.toLocaleString()} properties. Zoom in.`}
        className="flex items-center justify-center rounded-full border-2 border-white bg-primary font-semibold text-primary-foreground shadow-md"
        style={{ width: size, height: size, fontSize: size < 36 ? 11 : 13 }}
      >
        {cluster.count.toLocaleString()}
      </div>
    </AdvancedMarker>
  );
}

export function FitMapOnce({
  points,
  isLoading = false,
}: {
  points: MapFitPoint[];
  /** Skip framing while a refresh or first fetch is still in flight. */
  isLoading?: boolean;
}) {
  const map = useMap();
  const fittedRef = useRef(false);
  const [layoutReady, setLayoutReady] = useState(false);
  const zoomGuardRef = useRef<{ remove?: () => void } | null>(null);

  useEffect(() => {
    return () => {
      zoomGuardRef.current?.remove?.();
    };
  }, []);

  useEffect(() => {
    if (!map) return;
    const sync = () => {
      const ready = mapHasUsableSize(map);
      setLayoutReady((previous) => (previous === ready ? previous : ready));
    };
    sync();
    window.addEventListener("resize", sync);
    const listener = map.addListener?.("idle", sync);
    return () => {
      window.removeEventListener("resize", sync);
      listener?.remove?.();
    };
  }, [map]);

  useEffect(() => {
    if (!map || fittedRef.current) return;
    // An empty or in-flight list is not a boundary. Fitting it (or one stale
    // point that arrives before the refresh settles) zooms to street level.
    if (isLoading || points.length === 0 || !layoutReady) return;
    if (!mapHasUsableSize(map)) return;

    const bounds = new google.maps.LatLngBounds();
    for (const point of points) {
      extendBoundsForFitPoint(bounds, point);
    }
    if (bounds.isEmpty()) return;

    map.fitBounds(bounds, 48);
    fittedRef.current = true;
    zoomGuardRef.current?.remove?.();
    zoomGuardRef.current =
      google.maps.event?.addListenerOnce?.(map, "idle", () => {
        const zoom = map.getZoom();
        if (zoom != null && zoom > MAP_FIT_MAX_ZOOM) {
          map.setZoom(MAP_FIT_MAX_ZOOM);
        }
      }) ?? null;
  }, [isLoading, layoutReady, map, points]);

  return null;
}

export function LocalClusteredMarkers<T extends MapPoint>({
  points,
  renderPoint,
  onSummary,
  isLoading = false,
}: {
  points: T[];
  renderPoint: (point: T) => ReactNode;
  onSummary?: (summary: MapClusterSummaryState) => void;
  isLoading?: boolean;
}) {
  const viewport = useMapViewport();
  const view = useMemo(
    () => clusterMapPoints(points, viewport),
    [points, viewport],
  );
  useEffect(() => {
    onSummary?.({
      mode: view.mode,
      totalInView: view.totalInView,
      clusterCount: view.clusters.length,
    });
  }, [onSummary, view]);

  return (
    <>
      <FitMapOnce points={points} isLoading={isLoading} />
      {view.mode === "clusters"
        ? view.clusters.map((cluster) => (
            <MapCountClusterMarker key={cluster.id} cluster={cluster} />
          ))
        : view.pins.map((point) => renderPoint(point))}
    </>
  );
}

export function ServerClusteredMarkers<T extends MapPoint>({
  pins,
  clusters,
  renderPoint,
  onViewportChange,
  isLoading = false,
}: {
  pins: T[];
  clusters: MapCluster[];
  renderPoint: (point: T) => ReactNode;
  onViewportChange: (viewport: MapViewport) => void;
  isLoading?: boolean;
}) {
  useMapViewport(onViewportChange);
  const frame = clusters.length > 0 ? clusters : pins;
  return (
    <>
      <FitMapOnce points={frame} isLoading={isLoading} />
      {clusters.map((cluster) => (
        <MapCountClusterMarker key={cluster.id} cluster={cluster} />
      ))}
      {pins.map((pin) => renderPoint(pin))}
    </>
  );
}

export function MapClusterSummary({
  mode,
  totalInView,
  clusterCount,
}: {
  mode: "pins" | "clusters";
  totalInView: number;
  clusterCount: number;
}) {
  if (mode !== "clusters") return null;
  return (
    <div className="pointer-events-none absolute bottom-8 left-1/2 z-10 max-w-[90%] -translate-x-1/2 rounded-md bg-background/90 px-2 py-1 text-center text-xs text-muted-foreground shadow-sm ring-1 ring-border">
      {totalInView.toLocaleString()} properties grouped into{" "}
      {clusterCount.toLocaleString()} {clusterCount === 1 ? "area" : "areas"}.
      Zoom in to see each property.
    </div>
  );
}
